export type PendingAction = () => void;

export interface AuthGuardOptions {
  tab?: string;
  mode?: 'signin' | 'signup';
  notice?: string;
  actionName?: string;
}

type ModalOpenHandler = (tab?: string, mode?: 'signin' | 'signup', notice?: string) => void;

class AuthGuardServiceClass {
  private pendingAction: PendingAction | null = null;
  private pendingNotice: string | null = null;
  private pendingActionName: string | null = null;
  private modalHandler: ModalOpenHandler | null = null;

  public registerModalHandler(handler: ModalOpenHandler) {
    this.modalHandler = handler;
  }

  public unregisterModalHandler() {
    this.modalHandler = null;
  }

  /**
   * Main reusable Auth Guard method.
   * If user is authenticated, executes action immediately.
   * If not authenticated, saves the action and opens the auth modal.
   * After successful login, the stored action is automatically executed.
   */
  public requireAuth(action: PendingAction, options?: AuthGuardOptions) {
    const isLoggedIn = typeof window !== 'undefined' && localStorage.getItem('noor_auth_logged_in') === 'true';
    if (isLoggedIn) {
      action();
      return;
    }

    // Save action, action name and notice for execution after authentication
    this.pendingAction = action;
    this.pendingActionName = options?.actionName || null;
    this.pendingNotice = options?.notice || 'Sign in or create an account to complete your action.';

    // Trigger auth modal opening
    if (this.modalHandler) {
      this.modalHandler(options?.tab || 'account', options?.mode || 'signin', this.pendingNotice);
    }
  }

  /**
   * Executes the pending stored action automatically after successful login/signup.
   * Returns details about the executed action (if any).
   */
  public executePendingAction(): { actionName: string | null; notice: string | null } | null {
    if (this.pendingAction) {
      const actionToExecute = this.pendingAction;
      const details = {
        actionName: this.pendingActionName,
        notice: this.pendingNotice,
      };

      this.pendingAction = null;
      this.pendingActionName = null;
      this.pendingNotice = null;

      // Execute asynchronously so modal state updates cleanly without blocking
      setTimeout(() => {
        actionToExecute();
      }, 50);

      return details;
    }
    return null;
  }

  public clearPendingAction() {
    this.pendingAction = null;
    this.pendingActionName = null;
    this.pendingNotice = null;
  }

  public getPendingNotice(): string | null {
    return this.pendingNotice;
  }

  public getPendingActionName(): string | null {
    return this.pendingActionName;
  }

  public hasPendingAction(): boolean {
    return this.pendingAction !== null;
  }
}

export const authGuardService = new AuthGuardServiceClass();
